import { InlineAlert } from "~/components/design-system/components"
import { api } from "~/utils/api"
import { pluralize } from "~/utils/formatting"
import { usePackageDraft } from "./setup/packageDraft"

/**
 * The impact view before a delete is staged: what goes with it, then Mark for
 * deletion. Nothing is deleted until the draft is published.
 */
export function DeletionImpact({
  target,
  label,
  onCancel,
  onMarked,
}: {
  target: { packageId?: string; folderId?: string }
  label: string
  onCancel: () => void
  onMarked: () => void
}) {
  const draft = usePackageDraft()
  const impact = api.allocation.getPackageDeletionImpact.useQuery({
    packageIds: target.packageId ? [target.packageId] : [],
    folderIds: target.folderId ? [target.folderId] : [],
  })
  const data = impact.data
  const lines = data
    ? [
        target.folderId ? pluralize(data.packageCount, "package") : null,
        `${pluralize(data.variantCount, "variant")} · ${pluralize(data.skuCount, "SKU")}`,
        `${pluralize(data.storeCount, "store")} stop getting ${target.folderId ? "them" : "it"}`,
        `${pluralize(data.lineCount, "replenishment line")} (${pluralize(data.lineUnits, "unit")}) stop being suggested`,
        data.cartLineCount > 0
          ? `${pluralize(data.cartLineCount, "cart line")} lose their line`
          : null,
      ].filter((line): line is string => line !== null)
    : []
  return (
    <InlineAlert
      tone="destructive"
      size="block"
      title={`Delete ${label}?`}
      description={
        impact.isLoading ? (
          "Counting what goes with it…"
        ) : (
          <div className="flex flex-col gap-1">
            <span>Publishing removes:</span>
            <ul className="list-disc pl-5">
              {lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <span>
              {
                "It's marked for deletion first; Keep in its menu, or Discard all in the bar below, keeps it."
              }
            </span>
          </div>
        )
      }
      secondaryAction={{ label: "Cancel", onClick: onCancel }}
      action={{
        label: "Mark for deletion",
        onClick: () => {
          draft.markForDeletion(target)
          onMarked()
        },
        requires: "edit",
        disabled: impact.isLoading || draft.saving,
      }}
    />
  )
}
